import { translator } from "../../translation/translator";
import { ChoicesControlForm } from "./choices-control-form";
import { ChoicesControlRender } from "./choices-control-render";
import type { ChoicesInstance } from "./choices-control-utils";

export class ChoicesControlHandler {
    public static createClearHandler(select: HTMLSelectElement, instance: ChoicesInstance | null): EventListener {
        return (event) => {
            event.preventDefault();
            event.stopPropagation();
            ChoicesControlHandler.clearSelection(select, instance);
        };
    }

    public static clearSelection(select: HTMLSelectElement, instance: ChoicesInstance | null): void {
        ChoicesControlRender.renderNativeSelectionCleared(select);

        if (typeof instance?.removeActiveItems === "function") {
            instance.removeActiveItems();

            const input = ChoicesControlForm.getClonedInput(select, instance);
            ChoicesControlRender.renderInputValue(input, "");
            if (input instanceof HTMLInputElement) {
                input.dispatchEvent(new Event("input", { bubbles: true }));
            }

            select.dispatchEvent(new Event("change", { bubbles: true }));
            return;
        }

        select.dispatchEvent(new Event("change", { bubbles: true }));
    }

    public static createInnerClickHandler(inner: HTMLElement, instance: ChoicesInstance | null): EventListener {
        return (event) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            if (ChoicesControlForm.isChoicesButton(target)) return;
            if (ChoicesControlForm.isClearActionButton(target)) return;

            const input = ChoicesControlForm.getClonedInputFromRoot(inner);
            if (input) {
                input.focus();
            }

            if (typeof instance?.showDropdown === "function") {
                try {
                    instance.showDropdown();
                } catch {
                    // ignore
                }
            }
        };
    }

    public static createShowDropdownHandler(
        select: HTMLSelectElement,
        instance: ChoicesInstance | null,
    ): EventListener {
        return () => {
            const run = () => {
                const dropdown = ChoicesControlForm.getDropdown(select, instance);
                if (!(dropdown instanceof HTMLElement)) return;

                const scrollContainer = ChoicesControlForm.getClosestSearchMenuBody(select);
                if (!(scrollContainer instanceof HTMLElement)) return;

                const dropdownRect = dropdown.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const overflowBottom = dropdownRect.bottom - containerRect.bottom;
                const padding = 12;

                if (overflowBottom > 0) {
                    scrollContainer.scrollBy({ top: overflowBottom + padding, behavior: "smooth" });
                }
            };

            requestAnimationFrame(() => requestAnimationFrame(run));
        };
    }

    public static createSyncHandler(
        select: HTMLSelectElement,
        hidden: HTMLInputElement,
        bindFormat: string,
    ): EventListener {
        return () => {
            const values = ChoicesControlForm.getSelectedValues(select);
            const format = String(bindFormat || "json").toLowerCase();

            if (format === "scalar" || format === "single" || format === "first") {
                hidden.value = values[0] ?? "";
                return;
            }

            if (format === "csv") {
                hidden.value = values.join(",");
                return;
            }

            hidden.value = JSON.stringify(values);
        };
    }

    public static createUpdateUiStateHandler(
        select: HTMLSelectElement,
        instance: ChoicesInstance | null,
    ): EventListener {
        return () => {
            const selected = ChoicesControlForm.getSelectedValues(select);
            const hasItems = selected.length > 0;
            const controlRoot = ChoicesControlForm.getControlRoot(select);
            const choicesRoot = ChoicesControlForm.getChoicesRoot(select, instance);

            ChoicesControlRender.renderHasItemsState(controlRoot, choicesRoot, hasItems);

            if (!select.multiple) return;

            const input = ChoicesControlForm.getClonedInputFromRoot(choicesRoot);
            if (!(input instanceof HTMLInputElement)) return;

            const computed = select.dataset.choicesPlaceholder ?? input.getAttribute("placeholder") ?? "";
            const currentOriginal = input.dataset.placeholderOriginal ?? "";
            if ((!currentOriginal || currentOriginal.trim() === "") && computed) {
                ChoicesControlRender.renderPlaceholderOriginal(input, computed);
            }

            const placeholder = input.dataset.placeholderOriginal || computed || "";

            if (!hasItems && input.value) {
                ChoicesControlRender.renderInputValue(input, "");
                input.dispatchEvent(new Event("input", { bubbles: true }));
            }

            ChoicesControlRender.renderInputPlaceholder(input, hasItems ? "" : placeholder);
        };
    }

    public static createTranslatePlaceholderHandler(
        select: HTMLSelectElement,
        instance: ChoicesInstance | null,
    ): EventListener {
        return () => {
            const key = select.getAttribute("data-translate-choices-placeholder");
            if (key) {
                const translated = translator.translate(key);
                if (translated) {
                    ChoicesControlRender.renderChoicesPlaceholder(select, translated);
                }
            }

            const choicesRoot = ChoicesControlForm.getChoicesRoot(select, instance);
            if (choicesRoot && select.multiple) {
                const input = ChoicesControlForm.getClonedInputFromRoot(choicesRoot);
                if (input) {
                    ChoicesControlRender.renderPlaceholderOriginal(input, select.dataset.choicesPlaceholder ?? "");

                    const selected = ChoicesControlForm.getSelectedValues(select);
                    if (selected.length === 0) {
                        ChoicesControlRender.renderInputPlaceholder(input, input.dataset.placeholderOriginal ?? "");
                    }
                }
            }

            const onUpdate = ChoicesControlHandler.createUpdateUiStateHandler(select, instance);
            onUpdate(new Event("change"));
        };
    }
}
